<script setup lang="ts">
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Thumbs, Navigation } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'

import 'swiper/css'
import 'swiper/css/thumbs'
import 'swiper/css/navigation'

import type { INewsCarouselImage } from '~/components/entity/news'

const { t } = useI18n()
const props = defineProps<{
  images?: INewsCarouselImage[]
}>()

const thumbsSwiper = shallowRef<SwiperType | null>(null)
const isReady = ref(false)

const setThumbsSwiper = (swiper: SwiperType) => {
  thumbsSwiper.value = swiper
  isReady.value = true
}
</script>

<template>
  <div
    v-if="props.images && props.images.length > 0"
    v-show="props.images.length === 1 || isReady"
    class="space-y-4"
  >
    <div class="swiper-container-wrapper overflow-hidden rounded-lg">
      <Swiper
        :modules="[Thumbs, Navigation]"
        :thumbs="props.images.length > 1 ? { swiper: thumbsSwiper } : undefined"
        :navigation="props.images.length > 1"
        :slides-per-view="1"
        class="news-images-swiper"
      >
        <SwiperSlide v-for="(image, index) in props.images" :key="image.id">
          <NuxtImg
            :src="image.large"
            :alt="image.name || `${t('widget.news_images_slider.image')} ${index + 1}`"
            loading="lazy"
          />
        </SwiperSlide>
      </Swiper>
    </div>

    <Swiper
      v-if="props.images.length > 1"
      :modules="[Thumbs]"
      watch-slides-progress
      :slides-per-view="3"
      :space-between="12"
      :breakpoints="{
        640: {
          slidesPerView: 4,
          spaceBetween: 16
        },
        768: {
          slidesPerView: 6,
          spaceBetween: 16
        },
        1024: {
          slidesPerView: 7,
          spaceBetween: 16
        }
      }"
      class="thumbs-swiper"
      @swiper="setThumbsSwiper"
    >
      <SwiperSlide v-for="(image, index) in props.images" :key="image.id">
        <div class="thumb-wrapper cursor-pointer overflow-hidden rounded-lg">
          <NuxtImg
            :src="image.thumbnail"
            :alt="image.name || `${t('widget.news_images_slider.image')} ${index + 1}`"
            class="h-full w-full object-cover"
            loading="lazy"
          />
        </div>
      </SwiperSlide>
    </Swiper>
  </div>
</template>

<style scoped>
.swiper-container-wrapper {
  @apply relative;
}

.news-images-swiper {
  @apply relative;
  --swiper-navigation-color: #2d2d2d;
  --swiper-navigation-sides-offset: 12px;
}

.news-images-swiper :deep(.swiper-slide img) {
  @apply w-full rounded-lg object-cover;
}

/* Swiper navigation buttons styling */
.news-images-swiper :deep(.swiper-button-next),
.news-images-swiper :deep(.swiper-button-prev) {
  @apply h-10 w-10 rounded-full bg-white/70 opacity-0 shadow-lg transition-all duration-300;
}

.news-images-swiper :deep(.swiper-button-next):hover,
.news-images-swiper :deep(.swiper-button-prev):hover {
  @apply scale-110 bg-white;
}

.swiper-container-wrapper:hover .news-images-swiper :deep(.swiper-button-next),
.swiper-container-wrapper:hover .news-images-swiper :deep(.swiper-button-prev) {
  @apply opacity-100;
}

.news-images-swiper :deep(.swiper-button-next.swiper-button-disabled),
.news-images-swiper :deep(.swiper-button-prev.swiper-button-disabled) {
  @apply hidden;
}

.news-images-swiper :deep(.swiper-button-next)::after,
.news-images-swiper :deep(.swiper-button-prev)::after {
  @apply text-xs font-bold text-gray-800;
}

.news-images-swiper :deep(.swiper-navigation-icon) {
  @apply h-4 w-4;
}

.thumbs-swiper {
  @apply h-20 sm:h-24;
}

.thumb-wrapper {
  @apply relative h-full w-full;
}

.thumbs-swiper :deep(.swiper-slide) {
  @apply opacity-60 transition-opacity;
}

.thumbs-swiper :deep(.swiper-slide-thumb-active) {
  @apply opacity-100;
}
</style>
